<template>
  <div class="leaf-angle-container">
    <header class="header">
      <div class="clock">
        <span class="clock-time">{{ formattedTime }}</span>
        <span class="clock-ampm">{{ ampm }}</span>
      </div>
    </header>

    <main class="main-container">
      <div class="input-section">
        <input
          type="text"
          v-model="selected_crop"
          placeholder="Crop"
          class="input-field"
        />
        <input
          type="number"
          v-model="plot_no"
          placeholder="Plot#"
          class="input-field"
        />
        <p class="input-output">You entered: {{ selected_crop }} {{ plot_no }}</p>
      </div>

      <section class="orientation-section">
        <h4>Orientation</h4>
        <ul class="orientation-list">
          <li>
            Angle: <span>{{ obj_angle.toFixed(1) }}&deg;</span>
          </li>
          <div class="button-group">
            <button @click="toggleDebug" class="btn-toggle">
              {{ showDebug ? 'Hide Debug' : 'Show Debug' }}
            </button>
            <button id="download_button" class="btn btn-success" @click="download_csv">Download CSV</button>
          </div>
          <div v-if="showDebug" class="debug-info">
            <li>
              X-axis (&beta;): <span>{{ filtered_beta.toFixed(1) }}&deg;</span>
            </li>
            <li>
              Y-axis (&gamma;): <span>{{ filtered_gamma.toFixed(1) }}&deg;</span>
            </li>
            <li>
              Z-axis (&alpha;): <span>{{ filtered_alpha.toFixed(1) }}&deg;</span>
            </li>
            <li>
              <Compass :angleInDegrees="alpha - start_alpha" />
            </li>
            <li>
              <BubbleLevel :angleXInDegrees="gamma" :angleYInDegrees="beta" />
            </li>
          </div>
        </ul>
        <p v-if="!isIMUSupported" class="imu-warning">Please allow IMU access by pressing Start and refresh the screen</p>
      </section>

      <section v-if="showCamera" class="camera-section">
        <video id="video" class="camera-feed" autoplay></video>
        <canvas id="overlay" class="camera-overlay"></canvas>
        <p v-if="!isCameraSupported" class="camera-warning">Camera not supported in this browser.</p>
      </section>

      <section class="table-section">
        <table id="measurement-table">
          <thead>
            <tr>
              <th>Time</th>
              <th>Exp.</th>
              <th>#</th>
              <th>Rep.</th>
              <th>Angle</th>
              <!-- <th>X</th> -->
              <!-- <th>Y</th> -->
              <!-- <th>Z</th> -->
            </tr>
          </thead>
          <tbody>
            <tr v-for="(row, index) in rowList" :key="index">
              <td>{{ row.time }}</td>
              <td>{{ row.crop }}</td>
              <td>{{ row.plotNo }}</td>
              <td>{{ row.count }}</td>
              <td>{{ row.leafAngle.toFixed(1) }}</td>
              <!-- <td>{{ row.x.toFixed(1) }}</td>
              <td>{{ row.y.toFixed(1) }}</td>
              <td>{{ row.z.toFixed(1) }}</td> -->
            </tr>
          </tbody>
        </table>
      </section>
    </main>

    <footer class="fixed-button">
      <a
        id="start_demo"
        class="btn btn-success"
        href="#"
        role="button"
        @click="start_demo"
      >
        Start
      </a>
      <a
        id="record_demo"
        class="btn btn-record"
        href="#"
        role="button"
        @click="record_demo"
      >
        Record
      </a>
      <a
        id="undo_button"
        class="btn btn-undo"
        href="#"
        role="button"
        @click="undo_button"
      >
        Undo
      </a>
    </footer>
  </div>
</template>

<script>
import { saveAs } from 'file-saver';
import Compass from './CompassView.vue';
import BubbleLevel from './BubbleLevel.vue';

export default {
  name: 'LeafAngle',
  components: {
    Compass,
    BubbleLevel,
  },
  data() {
    return {
      is_running: false,
      alpha: 0,
      beta: 0,
      gamma: 0,
      start_alpha: 0,
      filtered_alpha: 0,
      filtered_beta: 0,
      filtered_gamma: 0,
      obj_angle: 0,
      counter: 0,
      selected_crop: 'EXP_NAME',
      prev_selected_crop: '',
      plot_no: 0,
      rowList: [],
      currentTime: null,
      formattedTime: '',
      ampm: '',
      showDebug: false,
      showCamera: false,
      Orientation_a: 0,
      Orientation_b: 0,
      Orientation_g: 0,
      isIMUSupported: true,
      isCameraSupported: true,
    };
  },
  mounted() {
    this.checkIMUSupport();
    this.updateTime();
    this.timer = setInterval(this.updateTime, 1000);
    this.startCamera();
  },
  beforeUnmount() {
    window.removeEventListener('deviceorientation', this.handleOrientation);
    clearInterval(this.timer);
    this.stopCamera();
  },
  methods: {
    checkIMUSupport() {
      if (!window.DeviceOrientationEvent) {
        this.isIMUSupported = false;
      } else if (typeof DeviceOrientationEvent.requestPermission === 'function') {
        DeviceOrientationEvent.requestPermission()
          .then((permissionState) => {
            if (permissionState === 'granted') {
              window.addEventListener('deviceorientation', this.handleOrientation);
            } else {
              this.isIMUSupported = false;
            }
          })
          .catch(() => {
            this.isIMUSupported = false;
          });
      } else {
        window.addEventListener('deviceorientation', this.handleOrientation);
      }
    },
    toggleDebug() {
      this.showDebug = !this.showDebug;
    },
    toggleCamera() {
      this.showCamera = !this.showCamera;
      if (this.showCamera) {
        //this.startCamera();
      } else {
        //this.stopCamera();
      }
    },
    updateFieldIfNotNull(fieldName, value) {
      if (value != null) {
        this[fieldName] = value;
      }
    },
    updateTime() {
      const now = new Date();
      const hours = now.getHours();
      const minutes = this.formatTimeUnit(now.getMinutes());
      const seconds = this.formatTimeUnit(now.getSeconds());

      this.ampm = hours >= 12 ? 'PM' : 'AM';
      const formattedHours = hours % 12 || 12;
      this.formattedTime = `${formattedHours}:${minutes}:${seconds}`;
    },
    formatTimeUnit(unit) {
      return unit.toString().padStart(2, '0');
    },
    generate_time_string() {
      const now = new Date();
      const year = now.getFullYear();
      const month = now.getMonth() + 1;
      const day = now.getDate();
      const hours = now.getHours();
      const minutes = now.getMinutes();
      const seconds = now.getSeconds();
      return `${month}/${day}/${year} ${hours}:${minutes}:${seconds}`;
    },
    handleOrientation(event) {
      const k = 0.9;

      if (this.is_running) {
        if (this.isIMUSupported) {
          this.alpha = event.alpha || 0;
          this.beta = event.beta || 0;
          this.gamma = event.gamma || 0;
        }

        if (
          this.filtered_alpha === 0 &&
          this.filtered_beta === 0 &&
          this.filtered_gamma === 0
        ) {
          this.filtered_alpha = this.alpha;
          this.filtered_beta = this.beta;
          this.filtered_gamma = this.gamma;
        } else {
          this.filtered_alpha = k * this.filtered_alpha + (1 - k) * this.alpha;
          this.filtered_beta = k * this.filtered_beta + (1 - k) * this.beta;
          this.filtered_gamma = k * this.filtered_gamma + (1 - k) * this.gamma;
        }

        this.obj_angle =
          this.filtered_beta > 0 && this.filtered_gamma > 0
            ? 180 - this.filtered_beta
            : this.filtered_beta > 0 && this.filtered_gamma < 0
            ? this.filtered_beta
            : this.obj_angle;

        if (this.obj_angle > 90) {
          this.obj_angle = 180 - this.obj_angle;
        }

        this.updateFieldIfNotNull('obj_angle', this.obj_angle);
        this.updateFieldIfNotNull('Orientation_a', this.filtered_alpha);
        this.updateFieldIfNotNull('Orientation_b', this.filtered_beta);
        this.updateFieldIfNotNull('Orientation_g', this.filtered_gamma);
      } else {
        this.reset_angles();
      }
    },
    start_demo(e) {
      e.preventDefault();
      console.log('start_demo');

      if (
        window.DeviceMotionEvent &&
        typeof DeviceOrientationEvent.requestPermission === 'function'
      ) {
        DeviceOrientationEvent.requestPermission()
          .then((permissionState) => {
            if (permissionState === 'granted') {
              window.addEventListener('deviceorientation', this.handleOrientation);
            }
          })
          .catch(console.error);
      } else {
        window.addEventListener('deviceorientation', this.handleOrientation);
      }

      const demo_button = document.getElementById('start_demo');
      if (this.is_running) {
        window.removeEventListener('deviceorientation', this.handleOrientation);
        demo_button.innerText = 'Start';
        demo_button.classList.add('btn-success');
        demo_button.classList.remove('btn-danger');
        this.is_running = false;
        this.reset_angles();
      } else {
        window.addEventListener('deviceorientation', this.handleOrientation);
        demo_button.innerText = 'Stop';
        demo_button.classList.remove('btn-success');
        demo_button.classList.add('btn-danger');
        this.is_running = true;
        this.start_alpha = this.filtered_alpha;
      }
    },
    record_demo(e) {
      e.preventDefault();
      console.log('record_demo');

      if (!this.is_running) {
        alert('Please press the Start button first!');
        return;
      }

      const newRow = {
        time: this.generate_time_string(),
        crop: this.selected_crop,
        plotNo: this.plot_no,
        count: this.counter + 1,
        x: this.filtered_alpha,
        y: this.filtered_beta,
        z: this.filtered_gamma,
        leafAngle: this.obj_angle,
      };

      if (this.selected_crop + this.plot_no !== this.prev_selected_crop) {
        this.counter = 0;
        this.prev_selected_crop = this.selected_crop + this.plot_no;
      }

      this.counter += 1;
      this.rowList.unshift(newRow);
      console.log(this.rowList); // 디버깅을 위해 추가
    },
    undo_button(e) {
      e.preventDefault();
      console.log('undo_button');

      if (this.rowList.length > 0) {
        this.rowList.shift();
        if (this.counter > 0) {
          this.counter -= 1;
        }
      }
    },
    download_csv() {
      const headers = [
        'Time',
        'Exp.',
        '#',
        'Rep.',
        'Leaf angle (Deg)',
        'Debug_X',
        'Debug_Y',
        'Debug_Z',
      ];
      const rows = this.rowList.map((row) => [
        row.time,
        row.crop,
        row.plotNo,
        row.count,
        row.leafAngle,
        row.x,
        row.y,
        row.z,
      ]).reverse(); // Reverse the order of rows

      const csvContent = [headers, ...rows].map((e) => e.join(',')).join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const downloadLink = document.createElement('a');
      downloadLink.href = url;

      const now = new Date();
      const year = now.getFullYear();
      const month = String(now.getMonth() + 1).padStart(2, '0');
      const day = String(now.getDate()).padStart(2, '0');
      const hours = String(now.getHours()).padStart(2, '0');
      const minutes = String(now.getMinutes()).padStart(2, '0');

      downloadLink.download = `${year}-${month}-${day}-${hours}-${minutes}.csv`;
      saveAs(blob, downloadLink.download);
    },
    reset_angles() {
      this.filtered_alpha = 0;
      this.filtered_beta = 0;
      this.filtered_gamma = 0;
      this.obj_angle = 0;
      this.alpha = 0;
      this.beta = 0;
      this.gamma = 0;

      this.updateFieldIfNotNull('Orientation_a', this.filtered_alpha);
      this.updateFieldIfNotNull('Orientation_b', this.filtered_beta);
      this.updateFieldIfNotNull('Orientation_g', this.filtered_gamma);
      this.updateFieldIfNotNull('obj_angle', this.obj_angle);
    },
    handleVolumeButton(event) {
      if (event.key === 'VolumeUp' || event.key === 'VolumeDown') {
        this.record_demo(event);
      }
    },
    startCamera() {
      this.$nextTick(() => {
        // 비디오 요소 선택
        const video = document.getElementById('video');
        console.log("Video element:", video);

        if (!video) {
          console.error("Video element not found");
          return;
        }

        // MediaDevices API의 getUserMedia 메서드를 사용하여 카메라에 접근
        if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
          console.log("Requesting camera access...");
          // 카메라 접근 요청
          navigator.mediaDevices.getUserMedia({ video: { facingMode: { exact: "environment" } } })
            .then((stream) => {
              console.log("Camera access granted, stream:", stream);
              // 비디오 요소의 srcObject를 카메라 스트림으로 설정
              video.srcObject = stream;
              video.onloadedmetadata = () => {
                console.log("Video metadata loaded");
                video.play();
                this.drawGuideline();
              };
            })
            .catch((error) => {
              console.error("Error accessing the camera:", error);
              if (error.name === "OverconstrainedError") {
                console.warn("Exact facingMode constraint not satisfied, trying without exact constraint.");
                // Fallback to less strict constraints
                const fallbackConstraints = {
                  video: {
                    facingMode: "environment"
                  }
                };
                return navigator.mediaDevices.getUserMedia(fallbackConstraints)
                  .then((stream) => {
                    console.log("Fallback camera access granted, stream:", stream);
                    video.srcObject = stream;
                    video.onloadedmetadata = () => {
                      console.log("Video metadata loaded (fallback)");
                      video.play();
                      this.drawGuideline();
                    };
                  })
                  .catch((error) => {
                    this.isCameraSupported = false;
                    console.error("Error accessing the camera (fallback):", error);
                  });
              } else {
                this.isCameraSupported = false;
                console.error("Error accessing the camera:", error);
              }
            });
        } else {
          this.isCameraSupported = false;
          console.log("getUserMedia는 이 브라우저에서 지원되지 않습니다.");
        }
      });
    },
    stopCamera() {
      const video = document.getElementById('video');
      const stream = video.srcObject;
      const tracks = stream.getTracks();

      tracks.forEach((track) => {
        track.stop();
      });

      video.srcObject = null;
    },
    drawGuideline() {
      const canvas = document.getElementById('overlay');
      const context = canvas.getContext('2d');
      const video = document.getElementById('video');

      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;

      context.strokeStyle = 'red';
      context.lineWidth = 2;

      const draw = () => {
        context.clearRect(0, 0, canvas.width, canvas.height);
        context.beginPath();
        context.moveTo(canvas.width / 2, 0);
        context.lineTo(canvas.width / 2, canvas.height);
        context.stroke();
        requestAnimationFrame(draw);
      };

      draw();
    },
  },
};
</script>

<style scoped>
.leaf-angle-container {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
  font-family: Arial, sans-serif;
}

.header {
  background-color: #f8f9fa;
  padding: 1rem;
  text-align: center;
  border-bottom: 1px solid #dee2e6;
}

.clock {
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 0.5rem;
}

.clock-time {
  font-size: 2rem;
  font-weight: bold;
}

.clock-ampm {
  font-size: 1rem;
}

.main-container {
  flex: 1;
  padding: 1rem;
  background-color: #e9ecef;
}

.input-section {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-bottom: 1rem;
}

.input-field {
  padding: 0.5rem;
  font-size: 1rem;
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
}

.input-output {
  margin-top: 0.5rem;
  font-size: 1rem;
  color: #495057;
}

.orientation-section {
  margin-bottom: 1.5rem;
}

.orientation-section h4 {
  margin-bottom: 0.5rem;
  font-size: 1.25rem;
  color: #343a40;
}

.orientation-list {
  list-style: none;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.orientation-list li {
  font-size: 1rem;
  color: #495057;
}

.button-group {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.btn-toggle {
  padding: 0.5rem 1rem;
  font-size: 0.9rem;
  color: #fff;
  background-color: #17a2b8;
  border: none;
  border-radius: 0.25rem;
  cursor: pointer;
}

.btn-toggle:hover {
  background-color: #138496;
}

.btn-success {
  padding: 0.5rem 1rem;
  font-size: 0.9rem;
  color: #fff;
  background-color: #28a745;
  border: none;
  border-radius: 0.25rem;
  cursor: pointer;
}

.btn-success:hover {
  background-color: #218838;
}

.btn-record {
  background-color: #ff9800; /* Orange */
}

.btn-record:hover {
  background-color: #e68900; /* Dark Orange */
}

.btn-undo {
  background-color: #2196f3; /* Blue */
}

.btn-undo:hover {
  background-color: #1976d2; /* Dark Blue */
}

.debug-info {
  margin-top: 0.5rem;
  padding: 1rem;
  background-color: #f1f3f5;
  border-radius: 0.25rem;
}

.imu-warning {
  color: red;
  font-size: 1rem;
  margin-top: 1rem;
}

.table-section {
  overflow-x: auto;
}

#measurement-table {
  width: 100%;
  border-collapse: collapse;
  background-color: #fff;
}

#measurement-table th,
#measurement-table td {
  padding: 0.5rem;
  text-align: left;
  border: 1px solid #dee2e6;
  font-size: 0.9rem;
}

#measurement-table th {
  background-color: #343a40;
  color: #fff;
}

.fixed-button {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.5rem;
  background-color: #f8f9fa;
  border-top: 1px solid #dee2e6;
  position: fixed;
  bottom: 0;
  width: 100%;
  z-index: 1000;
}

.btn {
  padding: 0.5rem 1rem;
  font-size: 1rem;
  border: none;
  border-radius: 0.25rem;
  cursor: pointer;
  text-decoration: none;
  text-align: center;
  color: #fff;
  flex: 1 1 30%;
  max-width: 30%;
}

@media (max-width: 768px) {
  .input-section {
    flex-direction: column;
  }

  .fixed-button {
    flex-direction: row;
    gap: 0.5rem;
  }

  .btn {
    flex: 1 1 30%;
    max-width: 30%;
  }

  .clock-time {
    font-size: 1.5rem;
  }

  .clock-ampm {
    font-size: 0.9rem;
  }

  .orientation-section h4 {
    font-size: 1.1rem;
  }

  .orientation-list li {
    font-size: 0.9rem;
  }

  .input-field {
    font-size: 0.9rem;
    padding: 0.4rem;
  }

  .btn-toggle {
    padding: 0.4rem 0.8rem;
    font-size: 0.8rem;
  }

  #measurement-table th,
  #measurement-table td {
    padding: 0.4rem;
    font-size: 0.8rem;
  }
}

.camera-section {
  position: relative;
  width: 100%;
  height: auto;
}

.camera-feed {
  width: 100%;
  height: auto;
}

.camera-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.camera-warning {
  color: red;
  font-size: 1rem;
  margin-top: 1rem;
}
</style>